/**
 * Composant DreamFilters
 * Permet de filtrer la liste des rêves par mots-clés et approches d'analyse
 * Utilise Material-UI Autocomplete pour une sélection multiple avec chips
 */

import React, { useState } from 'react';
import { 
  Box, 
  Chip, 
  TextField, 
  Autocomplete, 
  Typography,
  AutocompleteProps,
  SvgIconTypeMap,
  TypographyTypeMap,
  InputAdornment,
  Collapse,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import FilterListIcon from '@mui/icons-material/FilterList';
import LabelIcon from '@mui/icons-material/Label';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import { MdStorm, MdExpandMore } from "react-icons/md";
import { Dream } from '../../types/dream';
import { useTranslation } from 'react-i18next';

/**
 * Interface des props du composant
 * @param dreams - Liste des rêves à filtrer
 * @param selectedKeywords - Mots-clés actuellement sélectionnés
 * @param selectedApproaches - Approches actuellement sélectionnées
 * @param selectedEmotions - Émotions actuellement sélectionnées
 * @param searchQuery - Requête de recherche actuelle
 * @param onFilterChange - Callback appelé quand les filtres changent
 * @param onSearchChange - Callback appelé quand la requête de recherche change
 */
interface DreamFiltersProps {
  dreams: Dream[];
  selectedKeywords: string[];
  selectedApproaches: string[];
  selectedEmotions: string[];
  searchQuery: string;
  onFilterChange: (keywords: string[], approaches: string[], emotions: string[]) => void;
  onSearchChange: (query: string) => void;
}

const DreamFilters: React.FC<DreamFiltersProps> = ({
  dreams,
  selectedKeywords,
  selectedApproaches,
  selectedEmotions,
  searchQuery,
  onFilterChange,
  onSearchChange
}) => {
  const { t } = useTranslation();
  
  // Gestion responsive mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileCollapsed, setMobileCollapsed] = useState(true);
  
  const handleMobileToggle = () => {
    if (isMobile) {
      setMobileCollapsed(!mobileCollapsed);
    }
  };

  // Extraction de tous les mots-clés uniques des rêves
  const allKeywords = Array.from(new Set(
    dreams.flatMap(dream => dream.keywords || [])
  )).sort();

  // Extraction de toutes les approches uniques
  const allApproaches = Array.from(new Set(
    dreams.flatMap(dream => 
      (dream.approaches || [])
        .map(a => `${a.categoryName} - ${a.subcategoryName}`)
        .filter(approach => approach && approach.trim() !== '')
    )
  )).sort();

  // Extraction de toutes les émotions uniques
  const allEmotions = Array.from(new Set(
    dreams.flatMap(dream => 
      (dream.emotions || '').split(',').map(emotion => emotion.trim()).filter(Boolean)
    )
  )).sort();

  // Styles communs pour les champs de saisie
  const commonStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '5px',
      '& fieldset': {
        borderColor: 'rgba(104, 241, 209, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(104, 241, 209, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#68f1d1',
      }
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiInputBase-input': {
      color: 'white',
    },
  };

  // Styles pour les chips (tags)
  const chipStyles = {
    backgroundColor: 'rgba(104, 241, 209, 0.1)',
    color: '#68f1d1',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'rgba(104, 241, 209, 0.2)',
    }
  };

  return (
    <Box sx={{ pt:0, mb: 0 }}>
      {/* Titre principal des filtres - Cliquable sur mobile */}
      <Box 
        sx={{ 
          mb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isMobile ? 'pointer' : 'default',
          '&:hover': isMobile ? {
            backgroundColor: 'rgba(104, 241, 209, 0.0)'
          } : {},
          borderRadius: isMobile ? '8px' : 0,
          // Padding réduit sur mobile quand replié
          p: isMobile ? (mobileCollapsed ? 0.5 : 1) : 0,
          py: isMobile ? (mobileCollapsed ? 0.3 : 1) : 0
        }}
        onClick={handleMobileToggle}
      >
        <Typography variant={isMobile && mobileCollapsed ? "body1" : "h6"} sx={{ 
          color: '#68f1d1',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile && mobileCollapsed ? 0.5 : 1,
          fontWeight: isMobile && mobileCollapsed ? 400 : 500,
          fontSize: isMobile && mobileCollapsed ? '0.9rem' : undefined
        }}>
          <FilterListIcon sx={{ 
            fontSize: isMobile && mobileCollapsed ? '1.1rem' : '1.25rem' 
          }} /> 
          {t('dreamJournal.filters.title')}
        </Typography>
        
        {/* Icône expand/collapse - Visible uniquement sur mobile */}
        {isMobile && (
          <IconButton 
            sx={{ 
              color: '#68f1d1',
              p: mobileCollapsed ? 0.2 : 0.5,
              transform: mobileCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s'
            }}
          >
            <MdExpandMore size={mobileCollapsed ? "1rem" : "1.2rem"} />
          </IconButton>
        )}
      </Box>

      {/* Contenu des filtres - Repliable sur mobile */}
      <Collapse in={!isMobile || !mobileCollapsed} timeout={300}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: isMobile ? 1 : 0 }}>
        {/* Champ de recherche */}
        <TextField
          fullWidth
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('dreamJournal.filters.search')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#68f1d1' }} />
              </InputAdornment>
            ),
          }}
          sx={commonStyles}
        />

        {/* Filtre par mots-clés */}
        <Autocomplete
          multiple
          options={allKeywords}
          value={selectedKeywords}
          onChange={(_, newValue) => onFilterChange(newValue, selectedApproaches, selectedEmotions)}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                sx={chipStyles}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LabelIcon sx={{ color: '#68f1d1' }} />
                  <span>{t('dreamJournal.filters.keywords')}</span>
                </Box>
              }
              sx={commonStyles}
            />
          )}
        />

        {/* Filtre par approches */}
        <Autocomplete
          multiple
          options={allApproaches}
          value={selectedApproaches}
          onChange={(_, newValue) => onFilterChange(selectedKeywords, newValue, selectedEmotions)}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                sx={chipStyles}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon sx={{ color: '#68f1d1' }} />
                  <span>{t('dreamJournal.filters.approaches')}</span>
                </Box>
              }
              sx={commonStyles}
            />
          )}
        />

        {/* Filtre par émotions */}
        <Autocomplete
          multiple
          options={allEmotions}
          value={selectedEmotions}
          onChange={(_, newValue) => onFilterChange(selectedKeywords, selectedApproaches, newValue)}
          renderTags={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                sx={chipStyles}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MdStorm size="1.3rem" style={{ color: '#68f1d1' }} />
                  <span>{t('dreamJournal.filters.emotions')}</span>
                </Box>
              }
              sx={commonStyles}
            />
          )}
        />
        </Box>
      </Collapse>
    </Box>
  );
};

export default DreamFilters;
